import React from 'react';
import { QrCode, ExternalLink, ShieldCheck, ShoppingBag, ClipboardCopy } from 'lucide-react';
import { autobookPayUrl } from '../mock';

export default function PaymentSection({ variant = 'both', className = '' }) {
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(autobookPayUrl)}&size=360x360&bgcolor=ffffff&color=0a1f44&margin=8`;

  const label = {
    apparel: 'APPAREL',
    registration: 'REGISTRATION',
    both: 'APPAREL & REGISTRATION',
  }[variant] || 'PAYMENT';

  return (
    <section
      id="payment"
      data-testid="payment-section"
      className={`py-24 bg-[#0a1f44] relative overflow-hidden ${className}`}
    >
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, #c8102e 0, transparent 40%), radial-gradient(circle at 80% 80%, #c8102e 0, transparent 40%)',
        }}
      />
      <div className="relative max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">
            <ShieldCheck className="w-4 h-4" /> Secure Payment · Autobooks
          </div>
          <h2 className="font-display text-white text-5xl md:text-6xl leading-tight">
            PAY FOR {label}
          </h2>
          <p className="text-white/70 mt-4">
            One payment page for all Brookline JP Patriots {variant === 'both' ? 'apparel orders and player registrations' : (variant === 'apparel' ? 'apparel orders' : 'player registrations')}. Scan the QR with your phone camera or tap the button below.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center" data-testid="payment-section-grid">
          {/* QR code */}
          <div className="bg-white p-8 shadow-2xl mx-auto md:mx-0 md:ml-auto" data-testid="payment-qr">
            <img
              src={qrSrc}
              alt="Scan to pay via Autobooks"
              className="w-full max-w-[360px] h-auto"
            />
            <div className="mt-4 text-center">
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Scan to Pay</div>
              <div className="font-display text-[#0a1f44] text-xl mt-1">BROOKLINE-JP PATRIOTS</div>
            </div>
          </div>

          {/* Button + details */}
          <div className="text-white space-y-6">
            <div className="space-y-3">
              <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e]">Can't scan?</div>
              <a
                href={autobookPayUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="payment-button"
                className="group inline-flex items-center gap-3 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-sm px-8 py-5 shadow-lg shadow-[#c8102e]/40 hover:shadow-[#c8102e]/70 transition-all w-full md:w-auto justify-center"
              >
                <QrCode className="w-5 h-5" /> Pay via Autobooks
                <ExternalLink className="w-4 h-4 opacity-70 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>

            <div className="border border-white/10 p-4 space-y-3">
              <div className="font-heading uppercase tracking-[0.3em] text-[10px] text-[#c8102e]">Direct Link</div>
              <div className="flex items-center gap-2">
                <code
                  data-testid="payment-url"
                  className="flex-1 font-mono text-[11px] md:text-xs bg-black/30 px-2 py-2 text-white/90 select-all break-all"
                >
                  {autobookPayUrl}
                </code>
                <button
                  onClick={() => navigator.clipboard?.writeText(autobookPayUrl)}
                  data-testid="payment-copy-btn"
                  className="p-2 border border-white/20 hover:border-[#c8102e] text-white/70 hover:text-white transition-colors"
                  title="Copy payment link"
                >
                  <ClipboardCopy className="w-4 h-4" />
                </button>
              </div>
            </div>

            <ul className="space-y-2 text-white/80 text-sm">
              <li className="flex items-center gap-3"><ShoppingBag className="w-4 h-4 text-[#c8102e]" /> Apparel: hoodies, t-shirts, caps, and gear</li>
              <li className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-[#c8102e]" /> Registration: player fees for U5–U13 & Pee Wee</li>
              <li className="flex items-center gap-3"><QrCode className="w-4 h-4 text-[#c8102e]" /> Payments processed securely by Autobooks</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
